/**
 * Server connectivity tracker.
 *
 * `navigator.onLine` only knows if the device has a network interface — it
 * doesn't know if the Mon Ami server is actually reachable.  This module adds
 * a second layer: when any fetch fails with a NetworkError we mark the server
 * as unreachable and start polling the current_user endpoint until it responds.
 * Consumers should call `isOnline()` instead of reading `navigator.onLine`
 * directly.
 */

import { API_ENDPOINTS } from '../config/env';
import { getDeviceToken } from '@/db/services/device_service';
import { touchLastOnlineTime } from '@/db/services/offline_time_tracker';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let serverReachable = true; // optimistic start
let pollTimer: ReturnType<typeof setInterval> | null = null;

// Monotonic id for checkConnectivity calls. Concurrent checks can resolve out
// of order (e.g. a hung HEAD from an earlier 'online' event resolving after a
// newer successful check); only the LATEST call may write module state, so a
// stale failure can never clobber a fresh "reachable" verdict.
let checkSeq = 0;

// Connectivity "era". Constant in production; the test reset bumps it so an
// async failure from a fetch STARTED before the reset (a fire-and-forget
// analytics POST or prefetch from a previous test rejecting mid-test) can be
// recognized as stale and ignored instead of flipping serverReachable.
let connectivityEra = 0;

/** Capture at request start; pass to markServerUnreachable on failure. */
export function getConnectivityEra(): number {
  return connectivityEra;
}

const POLL_INTERVAL_MS = 15_000; // check every 15 s
const POLL_TIMEOUT_MS = 5_000;

/** Lightweight HEAD request to verify the server is reachable and session alive */
const CONNECTIVITY_URL = API_ENDPOINTS.currentUser;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns true when the browser reports a network connection AND the server
 * has been reachable (i.e. we haven't seen a recent NetworkError that hasn't
 * been resolved by a successful connectivity check).
 */
export function isOnline(): boolean {
  return navigator.onLine && serverReachable;
}

/**
 * Call this when a fetch fails with a network-level error (DNS failure,
 * connection refused, etc.).  It flips the reachable flag and starts
 * background polling so the app can recover automatically.
 */
export function markServerUnreachable(era?: number): void {
  if (era !== undefined && era !== connectivityEra) return; // failed fetch predates the current era — stale, ignore
  if (!serverReachable) return; // already tracking
  serverReachable = false;
  window.dispatchEvent(new CustomEvent('server-unreachable'));
  startPollLoop();
}

/**
 * Force a connectivity check right now (e.g. when the browser fires
 * the `online` event).  Resolves to the new reachability state. On failure
 * the recovery poll loop is (re)started, so a single failed probe can never
 * leave the app offline for good.
 */
export async function checkConnectivity(): Promise<boolean> {
  const seq = ++checkSeq;

  if (!navigator.onLine) {
    serverReachable = false;
    stopPollLoop();
    return false;
  }

  try {
    await fetch(CONNECTIVITY_URL, await connectivityRequestInit());
    // The device dropped off the network while the probe was in flight; the
    // `offline` handler already recorded that, and announcing "reachable"
    // now would start a sync that cannot succeed.
    if (!navigator.onLine) return false;
    // Any HTTP response proves the server was reached — 200, 401 (session
    // expired), 403 (device revoked), even a 5xx. A success is never stale:
    // the server answered, so it is reachable, whatever a concurrent probe
    // concluded. (The `online` event fans out to several callers at once.)
    // But only the latest probe may stop the recovery loop: if a newer probe
    // already failed and started it, the loop stays up until its own next
    // success confirms the verdict, so a slow-then-fast pair can never leave
    // us "online" with no way to notice we are not.
    void touchLastOnlineTime();
    markServerReachable({ stopPolling: seq === checkSeq });
    return true;
  } catch {
    // fall through to the unreachable handling below
  }

  if (seq !== checkSeq) return false; // superseded — the latest call owns the failure verdict

  // Unreachable. markServerUnreachable announces the transition (and starts
  // polling) only when we were reachable until now; the explicit
  // startPollLoop covers the case where we were already unreachable — after
  // a reload while offline or the `offline` event the loop is stopped, and
  // the `online` event fires only once, so if the probe it triggers fails
  // this loop is the only way the app ever comes back online.
  markServerUnreachable();
  startPollLoop();
  return false;
}

/**
 * Test-only: reset the module to its optimistic default (server reachable, no
 * poll loop). Component/e2e specs share this singleton across tests, so a
 * dangling fetch that rejects and calls markServerUnreachable() in one test
 * leaves `serverReachable` false for the next — making isOnline()-gated code
 * (pullDocument, the finalize refetch) spuriously bail. The Cypress support
 * hook calls this before each test.
 */
export function resetConnectivityStateForTests(): void {
  serverReachable = true;
  stopPollLoop();
  // Invalidate any in-flight checkConnectivity and any in-flight fetch so a
  // dangling call from a previous test resolving late cannot write state into
  // this test (the fetch layer captures the era at request start).
  checkSeq++;
  connectivityEra++;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build the HEAD request used by both checkConnectivity and the poll loop.
 * Prefers `Authorization: Bearer …` when a device token is stored — the
 * cookie path is only the transitional fallback for installs that haven't
 * registered a device yet. Otherwise the cookie has a much shorter useful
 * life than the bearer (online-app inactivity timeout vs. 30-day bearer
 * TTL) and the connectivity check spam-logs 401s when the cookie expires
 * while the bearer is still good.
 */
async function connectivityRequestInit(): Promise<RequestInit> {
  const token = await getDeviceToken().catch(() => null);
  const headers: Record<string, string> = {};
  let credentials: 'omit' | 'include' = 'include';
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    credentials = 'omit';
  }
  return {
    method: 'HEAD',
    headers,
    credentials,
    cache: 'no-store',
    signal: AbortSignal.timeout(POLL_TIMEOUT_MS),
  };
}

function markServerReachable({ stopPolling = true }: { stopPolling?: boolean } = {}): void {
  if (stopPolling) stopPollLoop();
  if (serverReachable) return;
  serverReachable = true;
  // Notify the rest of the app — components listening will pick this up
  // and resume normal operations (data refresh, document sync…).
  window.dispatchEvent(new CustomEvent('server-reachable'));
}

function startPollLoop(): void {
  if (pollTimer) return;

  pollTimer = setInterval(async () => {
    // If the browser itself is offline, no point polling
    if (!navigator.onLine) return;

    try {
      await fetch(CONNECTIVITY_URL, await connectivityRequestInit());
      // Any HTTP response means the server is reachable
      void touchLastOnlineTime();
      markServerReachable();
    } catch {
      // still unreachable — keep polling
    }
  }, POLL_INTERVAL_MS);
}

function stopPollLoop(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Browser event wiring
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    serverReachable = false;
    stopPollLoop();
  });

  window.addEventListener('online', () => {
    // Browser says we're back — verify with a real request before flipping the flag
    checkConnectivity();
  });
}
